import type { Extension } from '@codemirror/state';
import { EditorView, Decoration, type DecorationSet, ViewPlugin, ViewUpdate } from '@codemirror/view';
import { workbenchStore } from '~/lib/stores/workbench';
import { detectBrowser } from '~/utils/detectBrowser';

const secretPattern = /=(.+)$/gm;
const dotEnvPattern = /^\.env(\.(.*?))?$/;

const themeStyle = {
  '.cm-mask': {
    visibility: 'hidden',
    position: 'relative',
    '&::before': {
      content: 'attr(data-mask)',
      visibility: 'visible',
      position: 'absolute',
      top: '-2px',
      left: 0,
      transform: '',
    },
  },
};

if (!['Firefox', 'Safari'].includes(detectBrowser(navigator.userAgent).browserName)) {
  themeStyle['.cm-mask']['&::before'].position = 'relative';
  themeStyle['.cm-mask']['&::before'].transform = 'translate(0, 2px)';
}

const theme = EditorView.baseTheme(themeStyle);

function inRange(num: number, from: number, to: number) {
  return num >= from && num <= to;
}

const decorations = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    range: { from: number; to: number } = { from: -1, to: -1 };
    filePath: string = '';

    constructor(view: EditorView) {
      this.decorations = this._createDecorations(view);
    }

    update(update: ViewUpdate) {
      const range = update.state.selection.main;
      const rangeChanged = this.range.from !== range.from || this.range.to !== range.to;
      this.range = { from: range.from, to: range.to };

      const filePath = workbenchStore.currentDocument.get()?.filePath ?? '';
      const filePathChanged = this.filePath !== filePath;

      this.filePath = filePath;

      if (filePathChanged || rangeChanged || update.docChanged || update.viewportChanged) {
        this.decorations = this._createDecorations(update.view);
      }
    }

    private _createDecorations(view: EditorView) {
      const isDotenv = dotEnvPattern.test(this.filePath.split('/').at(-1) ?? '');

      if (!isDotenv) {
        return Decoration.set([]);
      }

      const decorations: [number, number, string][] = [];
      const text = view.state.doc.toString();

      for (const match of text.matchAll(secretPattern)) {
        const from = match.index + 1;
        const to = from + match[0].length - 1;

        const insideSecret = !inRange(this.range.from, from, to) && !inRange(this.range.to, from, to);

        const insideSelection =
          !inRange(from, this.range.from, this.range.to) && !inRange(to, this.range.from, this.range.to);

        if (insideSecret && insideSelection) {
          decorations.push([from, to, match[0].slice(1)]);
        }
      }

      return Decoration.set(
        decorations.map(([from, to, text]) =>
          Decoration.mark({ class: 'cm-mask', attributes: { 'data-mask': '*'.repeat(text.length) } }).range(from, to),
        ),
      );
    }
  },
  {
    decorations: (instance) => instance.decorations,
  },
);

export const dotEnvMaskExtension: Extension = [theme, decorations];
